import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import './ChartLayout.css';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface Props {
  location: string;
  geofips: number | null;
  preferredState?: string | null;
  yearStart?: number;
  yearEnd?: number;
}

type Row = {
  geofips: number;
  location_name: string;
  county: string | null;
  state: string;
  geography_type: 'state' | 'county' | 'place' | string;
  year: number;
  housing_units: number | null;
  num_employees: number | null;
};

export const HousingToEmployees: React.FC<Props> = ({
  location,
  geofips,
  preferredState = null,
  yearStart = 2012,
  yearEnd = 2023
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataRows, setDataRows] = useState<Row[]>([]);
  const [activeView, setActiveView] = useState<string>('overview');

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  useEffect(() => {
    const fetchData = async () => {
      // Add early return if location is not set
      if (!location || location === '') {
        console.log('No location set yet, waiting...');
        setLoading(false);
        return;
      }
      
      if (!geofips) {
        setError('A geofips is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching housing_to_employees data for location:', location, 'geofips:', geofips);
        console.log('Year range:', yearStart, 'to', yearEnd);
        
        let allData = null;
        let allErr = null;
        
        // Check if this is California state (geofips 6) - prioritize state query
        const isCaliforniaState = geofips === 6 || (location && location.toLowerCase() === 'california');
        
        if (isCaliforniaState) {
          console.log('Detected California state - querying state data directly');
          const { data: stateData, error: stateErr } = await supabaseClient
            .from('housing_to_employees')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geofips', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (stateErr) {
            console.error('Error fetching California state data:', stateErr);
            allErr = stateErr;
          }
          
          console.log('California state query result:', stateData?.length || 0, 'rows');
          if (stateData && stateData.length > 0) {
            console.log('Found California state data:', stateData.length, 'rows');
            console.log('Sample state data:', stateData[0]);
            allData = stateData;
          } else {
            console.log('No California state data found');
            allData = [];
          }
        } else {
          // Try place (city) data first, then county, then state
          let query = supabaseClient
            .from('housing_to_employees')
            .select('*')
            .eq('geography_type', 'place')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          // For cities, prioritize location_name match over geofips
          if (location) {
            console.log('Querying place data by location_name:', location);
            query = query.ilike('location_name', `%${location}%`);
          } else if (geofips) {
            console.log('Querying place data by geofips:', geofips);
            query = query.eq('geofips', geofips);
          }
          
          const { data: placeData, error: placeErr } = await query;
          
          if (placeErr) {
            console.error('Error fetching place data:', placeErr);
          }
          
          console.log('Place query result:', placeData?.length || 0, 'rows');
          if (placeData && placeData.length > 0) {
            console.log('Found place (city) data:', placeData.length, 'rows');
            console.log('Sample place data:', placeData[0]);
            allData = placeData;
          } else {
            console.log('No place data found, trying county data...');
            
            // Try county data
            let countyQuery = supabaseClient
              .from('housing_to_employees')
              .select('*')
              .eq('geography_type', 'county')
              .gte('year', yearStart)
              .lte('year', yearEnd)
              .order('year', { ascending: true });
            
            if (geofips) {
              console.log('Querying county data by geofips:', geofips);
              countyQuery = countyQuery.eq('geofips', geofips);
            } else if (location) {
              console.log('Querying county data by location_name:', location);
              countyQuery = countyQuery.ilike('location_name', `%${location}%`);
            }
            
            const { data: countyData, error: countyErr } = await countyQuery;
            
            if (countyErr) {
              console.error('Error fetching county data:', countyErr);
            }
            
            console.log('County query result:', countyData?.length || 0, 'rows');
            if (countyData && countyData.length > 0) {
              console.log('Found county data:', countyData.length, 'rows');
              console.log('Sample county data:', countyData[0]);
              allData = countyData;
            } else {
              console.log('No county data found, trying state data...');
              
              // Try state data
              let stateQuery = supabaseClient
                .from('housing_to_employees')
                .select('*')
                .eq('geography_type', 'state')
                .gte('year', yearStart)
                .lte('year', yearEnd)
                .order('year', { ascending: true });
              
              if (geofips) {
                console.log('Querying state data by geofips:', geofips);
                stateQuery = stateQuery.eq('geofips', geofips);
              } else if (location) {
                console.log('Querying state data by location_name:', location);
                stateQuery = stateQuery.ilike('location_name', `%${location}%`);
              }
              
              const { data: stateData, error: stateErr } = await stateQuery;
              
              if (stateErr) {
                console.error('Error fetching state data:', stateErr);
              }
              
              console.log('State query result:', stateData?.length || 0, 'rows');
              if (stateData && stateData.length > 0) {
                console.log('Found state-level data:', stateData.length, 'rows');
                console.log('Sample state data:', stateData[0]);
                allData = stateData;
              } else {
                console.log('No data found for any geography type');
                allData = [];
              }
              allErr = stateErr; // Assign the last error encountered
            }
          }
        }

        if (allErr) {
          console.error('Error fetching data:', allErr);
          throw allErr;
        }

        console.log('Total data found:', allData?.length || 0, 'rows');
        if (allData && allData.length > 0) {
          console.log('Sample data:', allData[0]);
          console.log('Geography types:', Array.from(new Set(allData.map(r => r.geography_type))));
          console.log('All data rows:', allData);
        } else {
          console.warn(`No housing to employees data found for geofips ${geofips} (${location})`);
        }
        
        // Use the data exactly as returned from the query - no additional filtering
        setDataRows(allData || []);
        setLoading(false);
      } catch (e: any) {
        console.error('Error loading housing_to_employees:', e);
        setError(e.message || 'Failed to load housing to employees data');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, yearStart, yearEnd, location]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || dataRows.length === 0) return null;
    
    const years = dataRows.map(r => r.year).filter(y => !isNaN(y));
    if (years.length === 0) return null;
    
    const latestYear = Math.max(...years);
    const latestRow = dataRows.find(r => r.year === latestYear);
    
    if (!latestRow || 
        latestRow.housing_units === null || 
        latestRow.housing_units === undefined ||
        latestRow.num_employees === null || 
        latestRow.num_employees === undefined) {
      return null;
    }
    
    const housingUnits = Number(latestRow.housing_units);
    const employees = Number(latestRow.num_employees);
    
    if (isNaN(housingUnits) || isNaN(employees)) {
      return null;
    }
    
    const ratio = employees > 0 
      ? (housingUnits / employees).toFixed(2)
      : 'N/A';
    
    return {
      housingUnits: housingUnits,
      employees: employees,
      ratio: ratio,
      latestYear: latestYear
    };
  }, [loading, dataRows]);

  // Build the chart after data loads
  useEffect(() => {
    if (loading || dataRows.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    console.log('Building chart with dataRows:', dataRows);
    console.log('Sample row:', dataRows[0]);

    // Prepare data for housing units and employees
    const housingData = years.map(year => {
      const row = dataRows.find(r => r.year === year);
      const value = row && row.housing_units !== null ? row.housing_units : null;
      if (value !== null) {
        console.log(`Year ${year}: housing_units = ${value}`);
      }
      return value;
    });

    const employeesData = years.map(year => {
      const row = dataRows.find(r => r.year === year);
      const value = row && row.num_employees !== null ? row.num_employees : null;
      if (value !== null) {
        console.log(`Year ${year}: num_employees = ${value}`);
      }
      return value;
    });

    console.log('Housing data array:', housingData);
    console.log('Employees data array:', employeesData);

    const chartTitle = `Housing Units and Employees - ${location || 'Selected Location'}`;

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: [
          {
            label: 'Housing Units',
            data: housingData,
            borderColor: '#3b82f6', // Blue
            backgroundColor: '#3b82f633',
            borderWidth: 2,
            spanGaps: true,
            tension: 0.25,
            pointRadius: 3
          },
          {
            label: 'Employees',
            data: employeesData,
            borderColor: '#ef4444', // Red
            backgroundColor: '#ef444433',
            borderWidth: 2,
            spanGaps: true,
            tension: 0.25,
            pointRadius: 3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: 'Count' },
            beginAtZero: true,
            ticks: {
              callback: (v: any) => {
                if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
                if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                return v.toString();
              }
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          title: standardTitle(chartTitle),
          legend: standardLegend,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toLocaleString() : 'N/A'}`;
              }
            }
          }
        }
      }
    });
  }, [loading, years, dataRows, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Housing_to_Employees');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const rows: Array<Record<string, any>> = dataRows.map(row => ({
      year: row.year,
      location: row.location_name,
      county: row.county,
      state: row.state,
      geography_type: row.geography_type,
      housing_units: row.housing_units,
      num_employees: row.num_employees
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'Housing_to_Employees_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading housing to employees data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  // If no location is set or no data found, don't show anything
  if (!location || (!loading && dataRows.length === 0)) {
    return null;
  }

  const chartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display
  const keyMetricsDisplay = keyMetrics && keyMetrics.housingUnits !== undefined && keyMetrics.employees !== undefined ? (
    <KeyMetrics>
      <KeyMetric
        value={keyMetrics.housingUnits.toLocaleString()}
        label={`Housing Units (${keyMetrics.latestYear})`}
        color="#3b82f6"
      />
      <KeyMetric
        value={keyMetrics.employees.toLocaleString()}
        label={`Employees (${keyMetrics.latestYear})`}
        color="#ef4444"
      />
      <KeyMetric
        value={keyMetrics.ratio}
        label="Housing Units per Employee"
        color="#10b981"
      />
    </KeyMetrics>
  ) : null;

  return (
    <ChartLayout
      title="Housing to Employees Ratio"
      key={`housing-employees-${location}`}
      description={`${location} • Source: US Census ACS 5-Year Estimates & ZIPCode/County Business Patterns`}
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Housing_to_Employees')}
      csvFilename={formatFilename(location, 'Housing_to_Employees_Data')}
      enableComments={true}
      location={location}
      overviewContent={
        <OverviewContent
          title="Jobs & Housing Balance"
          description="This chart compares the number of housing units to the number of employees over time, helping assess the jobs-housing balance in the area."
        >
          <div>
            {keyMetrics && keyMetrics.housingUnits !== undefined && keyMetrics.employees !== undefined ? (
              <div>
                <p>
                  As of {keyMetrics.latestYear}, there are {keyMetrics.housingUnits.toLocaleString()} housing units in {location} and {keyMetrics.employees.toLocaleString()} employees. 
                  This is a ratio of {keyMetrics.ratio}, meaning there are approximately {keyMetrics.ratio} housing units per employee in {location}.
                </p>
              </div>
            ) : (
              <div>
                <p>This chart shows the relationship between housing units and employment in the area over time.</p>
              </div>
            )}
          </div>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Policy Relevance"
          description="Comparing the number of housing units to employees helps assess the jobs-housing balance, a key factor in local climate action."
        >
          <div>
            <p>An imbalance, such as too few homes near job centers, can lead to longer commutes and higher emissions. This is especially challenging in areas with limited affordable housing, pushing workers farther from employment and worsening congestion and pollution. Similarly, residents of bedroom communities with no employment opportunities must commute to other jurisdictions for work.</p>
            <p>California's Regional Housing Needs Allocation (RHNA) and Housing Element Law require local governments to plan for housing growth in proportion to employment trends. Policies like SB 35 and AB 2011 streamline approvals for infill housing near jobs, while Government Code Section 65890.1 provides guidance on measuring and addressing jobs-housing fit. Jobs-housing would go beyond the jobs-housing balance to measure the extent to which the types and affordability of housing in a specific area match the needs of the local workforce.</p>
            <p>Analyzing this ratio helps cities adopt zoning and land use strategies that reduce emissions, lower commuting costs, and promote equitable, compact, and sustainable communities.</p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="A lack of affordable housing near job centers forces low- and moderate-income workers to live farther away, resulting in longer, more expensive commutes and greater exposure to transportation-related pollution."
        >
          <div>
            <p>This jobs-housing mismatch disproportionately affects communities of color and historically marginalized groups, who may also face limited access to reliable transit. Without an equity lens, climate policies, like promoting transit-oriented development or reducing car dependence, can unintentionally increase burdens on disadvantaged populations.</p>
            <p>By analyzing the housing-to-jobs ratio with equity in mind, local governments can target underserved areas, expand affordable housing near employment, and design climate strategies that reduce existing social and economic disparities.</p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="Housing units data is from US Census American Community Survey 5-Year Estimates. The number of employees data is from the ZIPCode Business Pattern (2012-2017) and County Business Pattern (2018-2023)."
          dataSources={[
            'US Census American Community Survey (ACS) 5-Year Estimates - Housing Units',
            'ZIPCode Business Pattern (2012-2017) - Employee counts',
            'County Business Pattern (2018-2023) - Employee counts',
            'Census relationship file for mapping ZIP Code to City'
          ]}
          methodology={[
            'Number of employees is mapped from ZIP Code to City based on relationship file from Census',
            'Housing units data comes from ACS 5-Year Estimates',
            'Ratio calculated as housing units divided by employees',
            'Time series analysis from 2012-2023 where data available',
            'Data filtered by geography type (place, county, or state) and location'
          ]}
        />
      }
    />
  );
};

export default HousingToEmployees;

